/**
 * transactionCards — shared data contract for every page that renders
 * transaction/booking cards (RenterDashboardPage, ProviderDashboardPage,
 * BookingsPage, InboxPage, and future surfaces).
 *
 * Five pages previously duplicated SDK include lists, `fields.image`
 * variant configs, state resolution, deleted-listing filtering, and
 * ghost-tx filtering. Every inconsistency between them was a bug:
 * missing `provider` included made host queries throw during
 * denormalisation; missing `imageVariant.listing-card` made photos
 * render as emoji fallbacks; InboxPage's `square-small` variant
 * wiped listing-card variants via Sharetribe's shallow-merge of image
 * attributes; no deleted-listing filter on host side meant em-dash
 * ghost cards; booking dates silently missing where the wrong access
 * pattern was still in use.
 *
 * This module centralises all of that. Adding a new card-rendering
 * page means calling `txCardQuery()` in the duck and `filterCardTxs()`
 * in the component — that's it. The class of bugs above can't recur.
 */
import { createImageVariantConfig } from './sdkLoader';
import { getProcess, resolveLatestProcessName } from '../transactions/transaction';

// ─── SDK query shape ─────────────────────────────────────────────────

/**
 * Canonical `include` list. Every card needs the full relationship
 * graph so denormalisation doesn't throw — notably `provider`, because
 * currentUser lives in state.user (not marketplaceData), so host-side
 * queries that forget this throw in denormalisedEntities.
 */
export const TX_CARD_INCLUDE = [
  'provider',
  'provider.profileImage',
  'customer',
  'customer.profileImage',
  'listing',
  'listing.images',
  'booking',
  'reviews',
];

/**
 * Image variant params. `fields.image` alone only returns
 * PRE-GENERATED variants; our marketplace images only have the
 * 'default' variant stored. `imageVariant.*` + `createImageVariantConfig`
 * tells Sharetribe to generate the variant on the fly (same pattern
 * SearchPage uses to render listing-card photos from search results).
 */
export const txCardImageParams = () => ({
  'fields.image': ['variants.listing-card', 'variants.listing-card-2x'],
  ...createImageVariantConfig('listing-card', 400, 1),
  ...createImageVariantConfig('listing-card-2x', 800, 1),
});

/**
 * Drop-in query builder for any page that renders tx cards.
 * Ducks call: `sdk.transactions.query(txCardQuery({ only: 'sale' }))`.
 *
 * InboxPage keeps its sparse `fields.transaction/listing/user` fieldsets
 * for page-weight reasons — it spreads `txCardQuery(...)` and then adds
 * those `fields.*` on top. The include list and image variant config
 * are still shared.
 */
export const txCardQuery = (extra = {}) => ({
  include: TX_CARD_INCLUDE,
  ...txCardImageParams(),
  perPage: 100,
  ...extra,
});

// ─── State resolution ───────────────────────────────────────────────

/**
 * Resolve the current state of a transaction via its process graph.
 * Sharetribe stores `lastTransition` (e.g. 'transition/accept'), not
 * the state name — filtering by `lastTransition` against state strings
 * like 'accepted' silently drops every transaction. Previously
 * duplicated in three ducks/components.
 */
export const getTxState = tx => {
  const processName = tx?.attributes?.processName;
  if (!processName) return null;
  try {
    const process = getProcess(resolveLatestProcessName(processName));
    return process?.getState ? process.getState(tx) : null;
  } catch (e) {
    // Unknown process — surface for debugging rather than silently dropping.
    // eslint-disable-next-line no-console
    console.warn('transactionCards: unknown transaction process', processName, e);
    return null;
  }
};

/**
 * Resolve the canonical process name of a transaction (e.g.
 * 'default-booking', 'default-negotiation'), collapsing legacy aliases
 * via resolveLatestProcessName. Returns null when processName is absent.
 *
 * Process-aware consumers key off this to disambiguate state names that
 * collide across processes. The one that matters today is 'delivered':
 * in default-booking it's the terminal "booking over" state ("Completed"),
 * while in default-negotiation it means the host delivered and the RENTER
 * must still accept the deliverable — an active order, not a finished one.
 * StatusBadge uses this to pick the right label.
 */
export const getTxProcessName = tx => {
  const processName = tx?.attributes?.processName;
  return processName ? resolveLatestProcessName(processName) : null;
};

// ─── Renter dashboard bucket classification ─────────────────────────
//
// SINGLE SOURCE OF TRUTH for which dashboard section a renter's
// transaction belongs in (Pending / Upcoming / Past), used by both the
// duck (which refs to store) and the component (which to render) so the
// two can't drift. Process-aware: a renter transacts on TWO processes —
// the booking process (event-kit-rental / default-booking) and the
// custom-quote negotiation process (default-negotiation) — and some
// state names collide across them with different meaning (e.g.
// 'delivered'), so classification must branch on the process, not the
// bare state string.

const NEGOTIATION_PROCESS_NAME = 'default-negotiation';

// Pending = an in-flight request, pre-fulfillment-order.
//   booking: `preauthorized` — requested + paid, awaiting host accept.
//   negotiation: `quote-requested` — awaiting the host's offer;
//     `offer-pending` / `update-pending` — host (re)sent an offer, the
//     renter's turn; `customer-offer-pending` — renter countered,
//     awaiting the host. StatusBadge distinguishes them ("Quote
//     requested" vs "Offer received" vs "Counter sent").
export const RENTER_PENDING_REQUEST_STATES = [
  'preauthorized',
  'quote-requested',
  'offer-pending',
  'update-pending',
  'customer-offer-pending',
];

// Upcoming = an active, confirmed order being fulfilled.
//   booking lists are preserved verbatim from the dashboard's original
//   inline lists (zero behaviour change for bookings).
//   negotiation: the offer is accepted/paid and moving to delivery.
const BOOKING_UPCOMING_STATES = [
  'accepted',
  // kit-ready was MISSING: the first live booking vanished from BOTH
  // dashboards the moment the host clicked Mark kit as ready
  // (2026-07-08, owner report). The completeness test below now pins
  // every process state to a bucket.
  'kit-ready',
  // v10: refund request under operator review — the booking is on hold
  // but alive (dates stay blocked), so it must remain visible on both
  // dashboards until resolved.
  'pickup-issue-review',
  'pickup-pending',
  'pickup-provider-confirmed',
  'pickup-renter-confirmed',
  'picked-up',
  'return-pending',
  'active-rental',
];
const NEGOTIATION_UPCOMING_STATES = [
  'pending-payment',
  'offer-accepted',
  'delivered',
  'changes-requested',
];

// Past = terminal / settled.
const BOOKING_PAST_STATES = [
  'completed',
  'cancelled-by-customer',
  'cancelled-by-customer-pickup-issue',
  'cancelled-pickup-refund-denied',
  'cancelled-by-provider',
  'canceled',
  'declined',
  'expired',
  'payment-expired',
  'reviewed',
  'reviewed-by-customer',
  'reviewed-by-provider',
  'disputed',
  'resolved-full',
  'resolved-partial',
  'return-late',
  'return-renter-submitted',
  'return-confirmed-pending-release',
  'damage-reported',
  'delivered',
];
const NEGOTIATION_PAST_STATES = [
  'completed',
  'reviewed',
  'reviewed-by-customer',
  'reviewed-by-provider',
  'offer-rejected',
  'request-rejected',
  'payment-expired',
  'canceled',
];

const isNegotiationTx = tx =>
  resolveLatestProcessName(tx?.attributes?.processName) === NEGOTIATION_PROCESS_NAME;

/**
 * True when a transaction is an outstanding renter request (Pending
 * Requests section). State-only — compose with isListingDeleted at the
 * call site, mirroring the other bucket filters.
 */
export const isRenterPendingRequest = tx =>
  RENTER_PENDING_REQUEST_STATES.includes(getTxState(tx));

/**
 * True when a transaction is an active, confirmed renter order
 * (Upcoming Rentals). Process-aware. Pair with the call site's
 * isBookingEnded() check to drop bookings whose window has passed.
 */
export const isRenterUpcoming = tx => {
  const list = isNegotiationTx(tx) ? NEGOTIATION_UPCOMING_STATES : BOOKING_UPCOMING_STATES;
  return list.includes(getTxState(tx));
};

/**
 * True when a transaction is settled / terminal for the renter (Past
 * Rentals). Process-aware.
 */
export const isRenterPast = tx => {
  const list = isNegotiationTx(tx) ? NEGOTIATION_PAST_STATES : BOOKING_PAST_STATES;
  return list.includes(getTxState(tx));
};

// ─── Host-side buckets (2026-07-02) ─────────────────────────────────
//
// The host dashboard/bookings surfaces bucketed by booking-process
// states only, so quote requests never appeared as pending requests —
// they read as bare inbox messages (owner-reported). Owner rule:
// NOTHING is "Upcoming" unless it's a confirmed (paid/accepted)
// booking — everything still in flight lives under Pending, whichever
// side's turn it is. StatusBadge distinguishes whose move it is
// ("New quote request" vs "Offer sent").

export const isHostPendingRequest = tx => {
  const list = isNegotiationTx(tx)
    ? [
        'quote-requested', // host's turn: send an offer
        'customer-offer-pending', // host's turn: respond to a counter
        'offer-pending', // renter's turn: offer is out
        'update-pending', // renter's turn: revised offer is out
        'pending-payment', // renter mid-checkout, not yet confirmed
      ]
    : ['preauthorized', 'pending-payment'];
  return list.includes(getTxState(tx));
};

export const isHostUpcoming = tx => {
  const list = isNegotiationTx(tx)
    ? NEGOTIATION_UPCOMING_STATES.filter(s => s !== 'pending-payment')
    : BOOKING_UPCOMING_STATES;
  return list.includes(getTxState(tx));
};

export const isHostPast = isRenterPast;

// ─── Filters ────────────────────────────────────────────────────────

/**
 * True when the tx's listing is deleted or unresolvable. Requires the
 * listing relationship to be denormalised (i.e. `listing` in the duck's
 * `include` list AND the listing entity in marketplaceData).
 *
 * Deleted listings are returned by Sharetribe with `attributes.deleted: true`.
 * Fully hard-deleted listings (404 on show) come back with the listing
 * ref but no entity — denormalised `tx.listing` is then null/undefined.
 * Both shapes map to "deleted" for card purposes.
 */
export const isListingDeleted = tx => {
  const listing = tx?.listing;
  return !listing || listing.attributes?.deleted === true;
};

/**
 * True when the tx's state can't be resolved (unknown process, malformed
 * data). Rendered cards would have no meaningful status badge, so we
 * hide these rather than show a "?" card.
 */
export const isUnresolvedTx = tx => getTxState(tx) == null;

/**
 * Belt-and-suspenders filter: keep only transactions where the given
 * role field on the tx matches the current user.
 *
 * Why needed: getMarketplaceEntities resolves refs from a SHARED entity
 * store. Other pages can populate the store with transactions the
 * current user isn't the right role on. Even if our own SDK query has
 * `only: 'sale'`, refs are stored by id — a stale ref from a prior
 * render could theoretically bleed through. Gabrielle's renter-side
 * rental once surfaced on her host dashboard for exactly this reason.
 *
 * @param {Array} txs        denormalised transactions
 * @param {'provider'|'customer'} role
 * @param {string} currentUserId  currentUser.id.uuid
 */
export const onlyRoleSide = (txs, role, currentUserId) => {
  if (!currentUserId) return [];
  const field = role === 'customer' ? 'customer' : 'provider';
  return txs.filter(tx => tx?.[field]?.id?.uuid === currentUserId);
};

/**
 * The one-shot filter every card list should apply. Composes the three
 * safety filters above. Pages that need ONLY some of them (e.g. to
 * show "deleted listing" placeholders) should compose the individual
 * helpers instead.
 */
export const filterCardTxs = (txs, { role, currentUserId }) =>
  onlyRoleSide(txs, role, currentUserId)
    .filter(tx => !isListingDeleted(tx) && !isUnresolvedTx(tx));

// ─── Booking date helpers ───────────────────────────────────────────

/**
 * `booking` is a separate denormalised entity at `tx.booking` — its
 * start/end live under `tx.booking.attributes`. Earlier versions of
 * several files read `tx.attributes.booking?.start`, which is always
 * undefined (booking is a relationship, not an attribute). That quietly
 * stripped date ranges from BookingsPage cards among others.
 */
// Prefer `displayStart` / `displayEnd` over raw `start` / `end`. For
// day-based bookings, Sharetribe stores `start`/`end` as UTC instants
// while `displayStart`/`displayEnd` are in the listing's timezone — the
// timezone the host meant when picking the day. Using the UTC instant
// for "is this today?" / "next 7 days" math flips a Tue-midnight-PDT
// booking into Monday for any user east of UTC-7, producing the B5
// off-by-one ("rental is today (Monday)" for an Apr 28 booking when
// today is Apr 27). InboxPage's bookingData already prefers
// displayStart; this brings the card-level helpers in line.
//
// For hourly / non-day bookings, displayStart equals start, so no
// behavioral change.
export const getBookingStart = tx =>
  tx?.booking?.attributes?.displayStart || tx?.booking?.attributes?.start || null;
export const getBookingEnd = tx =>
  tx?.booking?.attributes?.displayEnd || tx?.booking?.attributes?.end || null;

/**
 * True when the booking's end date has passed. Used by page-level
 * bucketing to move transactions stuck in an "upcoming" process state
 * (e.g. `accepted`, `active-rental`) out of the Upcoming section once
 * the rental window is over.
 *
 * Why: the state-based Upcoming filter was modeled on the happy-path
 * progression (accepted → picked-up → completed). Real data can sit
 * indefinitely in a middle state — test transactions that never get
 * auto-progressed, abandoned bookings, etc. State-only bucketing
 * treated those as "still upcoming" even when the booking was clearly
 * over. Pair this check with the state filter so an expired booking
 * always falls into Past, regardless of process state.
 *
 * Returns false for transactions without booking dates (e.g. inquiries)
 * so those aren't accidentally re-classified.
 *
 * @param {Object} tx denormalised transaction (tx.booking must exist)
 * @param {Date} [now] injected for testability; defaults to new Date()
 */
export const isBookingEnded = (tx, now = new Date()) => {
  const end = getBookingEnd(tx);
  if (!end) return false;
  return new Date(end) < now;
};

// ─── Image helpers ──────────────────────────────────────────────────

/**
 * Pick the listing-card variant keys off a denormalised image entity.
 * Required because our SDK query asks Sharetribe to generate variants
 * at query time; the resulting variants map is dynamic. Consumers pass
 * this array to <ResponsiveImage variants={...} />.
 *
 * Returns [] when the image entity has no listing-card variants (e.g.
 * the listing uploaded without photos, OR the store entry was merged
 * from a query that only asked for different variants). Consumers
 * should fall back to an emoji or placeholder in that case.
 */
export const listingCardImageVariants = image =>
  Object.keys(image?.attributes?.variants || {}).filter(k =>
    k.startsWith('listing-card')
  );

/**
 * Convenience: pull the listing's first image and its variant keys.
 * Returns { firstImage, variants }. firstImage is null when the listing
 * has no images.
 */
export const getListingCardImage = listing => {
  const firstImage = listing?.images?.[0] || null;
  const variants = firstImage ? listingCardImageVariants(firstImage) : [];
  return { firstImage, variants };
};

// ─── Service-type label ────────────────────────────────────────────

/**
 * Short labelled chip for the booking's service type, derived from
 * tx.attributes.protectedData.deliveryMethod. Returns null when the
 * field is absent or unknown so card layouts can skip rendering.
 *
 * 'shipping' was the legacy alias for 'delivery' before PR 1; treat
 * the same here so older bookings still render correctly. Same
 * pattern for 'whiteGlove': it was the value written into
 * deliveryMethod before the 2026-05-13 rename to 'setup'. Transaction
 * protectedData is immutable historical data so we cannot backfill;
 * the read recognises both. New transactions write 'setup' (via
 * OrderPanel's selectedFulfillment, which reflects the renamed
 * fulfillment enum value).
 */
export const getServiceLabel = tx => {
  const method = tx?.attributes?.protectedData?.deliveryMethod;
  if (method === 'setup' || method === 'whiteGlove') return '🛠 Setup';
  if (method === 'delivery' || method === 'shipping') return '🚚 Delivery';
  if (method === 'pickup') return '📍 Pickup';
  return null;
};

// test-only export: the bucket completeness test pins every process
// state to a bucket (see transactionCards.buckets.rte.test.js)
export const _testing = {
  RENTER_PENDING_REQUEST_STATES,
  BOOKING_UPCOMING_STATES,
  NEGOTIATION_UPCOMING_STATES,
  BOOKING_PAST_STATES,
  NEGOTIATION_PAST_STATES,
};
